<template>
  <div class="autocomplete" :class="containerClassName">
    <label :for="id">{{label}}<span v-if="required"> *</span></label>

    <div class="input-container" :class="inputContainerClasses">
      <div class="vue-treeselect custom vue-treeselect--single vue-treeselect--searchable vue-treeselect--open-below" :class="[mainTreeSelectClass]">
        <div class="vue-treeselect__control">
          <div class="vue-treeselect__value-container">
            <div class="vue-treeselect__single-value">
              <div></div>
            </div>

            <div class="vue-treeselect__placeholder vue-treeselect-helper-zoom-effect-off" :class="{'vue-treeselect-helper-hide': (value !== '' && value !== null)}">{{placeholder}}</div>
            <div class="vue-treeselect__input-container">
              <input
                type="text"
                class="vue-treeselect__input"
                :id="id"
                :name="name"
                :value="value"
                :class="className"
                :disabled="isDisabled"
                autocomplete="off"
                tabindex="0"
                @keydown="onSearchInput"
                @input="onInput"
                @focus="onFocus"
                @blur="onBlur"
              >
            </div>
          </div>

          <div class="vue-treeselect__x-container" title="Clear value" @click="onClearInput()"></div>
          <div class="vue-treeselect__control-arrow-container"></div>
        </div>

        <div class="vue-treeselect__menu-container" style="z-index: 999;">
          <div class="vue-treeselect__menu vue-treeselect__menu--transition-leave vue-treeselect__menu--transition-leave-active">
            <div class="vue-treeselect__list">
              <div v-if="items.length < 1 || searching" class="vue-treeselect__list-item vue-treeselect__indent-level-0">
                <div class="vue-treeselect__label-container">
                  <label class="vue-treeselect__label">{{ noItemsText || 'Laster...'}}</label>
                </div>
              </div>

              <template v-if="items.length > 0">
                <div v-for="(item, index) in items" v-bind:key="index" class="vue-treeselect__list-item vue-treeselect__indent-level-0">
                  <div class="vue-treeselect__option" :data-item-index="index" @click.prevent="onSelectItem(index, item)">
                    <div class="vue-treeselect__label-container">
                      <label class="vue-treeselect__label">
                        <slot name="item" :item="item">
                            {{item}}
                        </slot>
                      </label>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <span class="form-error-label">
        <svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg>
        {{ customErrorMessage ? customErrorMessage : errors.first(name) }}
      </span>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import DummyCache from '@/plugins/dummy-cache.js'

export default {
  name: 'TheTagsAutoCompleteInput',
  inject: [
    '$validator'
  ],
  inheritAttributes: true,
  props: {
    id: {
      type: String,
      default: function () {
        return 'autocomplete-' + this._uid
      }
    },
    containerClassName: {
      type: String,
      default: 'cell large-12'
    },
    customErrorMessage: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: null
    },
    value: {
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Søk'
    },
    disabled: {
      type: [Boolean, Number],
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: 'Søk'
    },
    className: {
      type: String,
      required: false,
      default: 'searchinput'
    },
    required: {
      type: Boolean,
      default: false
    },
    minquery: {
      type: Number,
      required: false,
      default: 1
    },
    debounce: {
      type: Number,
      required: false,
      default: 500
    },
    onInputCallback: {
      type: Function,
      required: true
    },
    onSelectCallback: {
      type: Function,
      required: true
    },
    quicklinks: {
      type: Array,
      required: false,
      default: null
    }
  },
  data () {
    return {
      uuid: 0,
      items: [],
      cache: null,
      searching: false,
      nothingfound: false,
      hasFocus: false
    }
  },
  created () {
    // Create a cache container
    this.cache = new DummyCache()
  },
  beforeMount () {
    // Make debounce
    if (this.debounce) {
      this.onSearchInput = _.debounce(this.onSearchInput, this.debounce)
    }

    // On click outside
    var vm = this
    window.jQuery(document).mouseup(function (e) {
      var container = window.jQuery('.autocomplete')
      // if the target of the click isn't the container nor a descendant of the container
      if (!container.is(e.target) && container.has(e.target).length === 0) {
        vm.hasFocus = false
      }
    })
  },
  computed: {
    isDisabled: function () {
      return this.disabled || this.searching
    },
    noItemsText: function () {
      if (this.searching) {
        return 'Søker...'
      }

      if (this.nothingfound) {
        return 'Ingen tagger funnet...'
      }

      if (this.items.length > 0) {
        return ''
      }

      return 'Skriv noe for å søke...'
    },
    mainTreeSelectClass: function () {
      let cls = []

      if (this.hasFocus) {
        cls.push('vue-treeselect--open')
        cls.push('vue-treeselect--search-active')
      }

      if (this.value !== '' && this.value !== null) {
        cls.push('vue-treeselect--has-value')
      }

      return cls.length > 0 ? cls.join(' ') : ''
    },
    inputContainerClasses: function () {
      let cls = []

      if (this.errors.has(this.name)) {
        cls.push('form-error')
      }

      if (this.quicklinks && this.quicklinks.length > 0) {
        cls.push('quick-suggestions')
      }

      return cls
    }
  },
  methods: {
    fixString: function (e) {
      let splitted = this.$helpers.explode(e.target.value, ',')
      splitted = splitted.filter((item, pos, self) => {
        return self.indexOf(item) === pos
      })

      setTimeout(function () {
        e.target.value = splitted.join(', ')
        e.target.value = e.target.value.replace(/ +/, ' ')
        e.target.value = e.target.value.replace(/, /, ',')
        e.target.value = e.target.value.replace(/,+/, ', ')
        e.target.value = this._.trim(e.target.value, ', ') + ', '
      })
    },
    onFocus: function (e) {
      this.hasFocus = true
    },
    onBlur: function (e) {
      e.target.value = e.target.value.replace(/ +/, ' ')
      e.target.value = e.target.value.replace(/, +/, ',')
      e.target.value = e.target.value.replace(/,+/, ', ')
      e.target.value = this._.trim(e.target.value, ', ')

      this.$emit('input', e.target.value)
    },
    onInput: function (e) {
      this.$emit('input', e.target.value)
    },
    onClearInput: function () {
      this.$emit('input', '')

      if (this.onResetCallback !== undefined) {
        this.onResetCallback()
      }

      this.hasFocus = false
      this.items = []
    },
    onSelectItem: function (index, item) {
      window.jQuery('.vue-treeselect__option').removeClass('vue-treeselect__option--selected')
      window.jQuery('[data-item-index="' + index + '"]').addClass('vue-treeselect__option--selected')

      // Run select callback
      this.onSelectCallback(item)

      console.log('On select')
      this.hasFocus = false
      this.items = []
      let element = window.jQuery('input#' + this.id)

      // Must have timeout around this for it to work.
      setTimeout(function () {
        // Put element in focus again...
        element.focus()
      })
    },
    onSearchInput: function (e) {
      if (e.keyCode === 188 || e.keyCode === 110 || e.keyCode === 32) {
        this.fixString(e)

        this.items = []
        return
      }

      // Create a promise variable
      let promise = null

      // Trim value
      let trimmed = this._.toLower(e.target.value.trim())

      // console.log(trimmed, trimmed.length)

      // No value - reset
      if (trimmed.length < 1) {
        // No query, so reset selected
        this.onClearInput()
        return
      } else if (trimmed.length < this.minquery) {
        this.items = []
        return
      }

      this.nothingfound = false
      this.searching = true

      promise = this.onInputCallback(trimmed)

      // Do things with promise
      promise
        .then((data) => {
          this.items = data.data || []
        })
        .catch((error) => {
          console.log('Failed Loading')
          console.log(error)
        })
        .then(() => {
          this.searching = false
          this.nothingfound = this.items.length < 1

          let element = window.jQuery('input#' + this.id)

          if (this.nothingfound) {
            this.fixString(e)
          }

          // Must have timeout around this for it to work.
          setTimeout(function () {
            // Put element in focus again...
            element.focus()
          })
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Inputs/TheTagsAutoCompleteInput.vue